
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue'
import draggable from 'vuedraggable'
import { useStore } from '@/store'
import { useRouter, useRoute } from 'vue-router'
import { CourseCatalogItem, OrgListItem } from '@/store/superAdmin'
import {
  listLessons,
  createLesson,
  updateLesson,
  deleteLesson,
  extractScormLesson,
  publishDraftLesson,
  discardDraftLesson,
  CourseLesson,
} from '@training24/course/src/lesson'
import { getEnvironmentVariables } from '@training24/course/src/environmentVariables'

export default defineComponent({
  name: 'CourseFormView',
  components: { draggable },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()

    const courseId = computed(() => route.params.courseId as string | undefined)
    const isEdit = computed(() => !!courseId.value)

    const form = ref<Partial<CourseCatalogItem>>({
      courseId: '',
      title: '',
      description: '',
      category: '',
      durationMinutes: 0,
      s3Key: '',
      imageUrl: '',
      provider: 'mindsmith',
      published: false,
    })

    const loadError = ref<string | null>(null)
    const saveError = ref<string | null>(null)
    const saveSuccess = ref(false)
    const saving = ref(false)

    // ── SCORM Upload ─────────────────────────────────────────
    const uploadFile = ref<File | null>(null)
    const uploadLoading = ref(false)
    const uploadProgress = ref(0)
    const uploadStatus = ref<'idle' | 'success' | 'error'>('idle')

    const uploadTargetKey = computed(() => {
      if (!uploadFile.value) return ''
      const id = form.value.courseId || 'kurs'
      return `scorm/${id}/${uploadFile.value.name}`
    })

    // ── Org Visibility ────────────────────────────────────────
    const visForAll = ref(true)
    const selectedOrgs = ref<OrgListItem[]>([])
    const visLoading = ref(false)

    // Org picker modal
    const orgPickerVisible = ref(false)
    const orgPickerLoading = ref(false)
    const orgSearch = ref('')

    const allOrgs = computed<OrgListItem[]>(() => store.getters['superAdmin/getAllOrganisations'])

    const filteredPickerOrgs = computed(() => {
      const q = orgSearch.value.toLowerCase().trim()
      if (!q) return allOrgs.value
      return allOrgs.value.filter(
        (o) => o.name.toLowerCase().includes(q) || o.organisationId.toLowerCase().includes(q)
      )
    })

    function isOrgSelected(orgId: string) {
      return selectedOrgs.value.some((o) => o.organisationId === orgId)
    }

    function toggleOrg(org: OrgListItem) {
      if (isOrgSelected(org.organisationId)) {
        selectedOrgs.value = selectedOrgs.value.filter((o) => o.organisationId !== org.organisationId)
      } else {
        selectedOrgs.value.push(org)
      }
    }

    function removeOrg(orgId: string) {
      selectedOrgs.value = selectedOrgs.value.filter((o) => o.organisationId !== orgId)
    }

    async function openOrgPicker() {
      orgPickerVisible.value = true
      orgSearch.value = ''
      if (!store.getters['superAdmin/getAllOrganisations'].length) {
        orgPickerLoading.value = true
        try {
          await store.dispatch('superAdmin/LOAD_ALL_ORGANISATIONS')
        } finally {
          orgPickerLoading.value = false
        }
      }
    }

    // ── Lessons ──────────────────────────────────────────────
    const lessons = ref<CourseLesson[]>([])
    const lessonsLoading = ref(false)

    // Lesson modal
    const lessonModalVisible = ref(false)
    const lessonModalMode = ref<'create' | 'edit'>('create')
    const editingLesson = ref<CourseLesson | null>(null)
    const lessonForm = ref({ title: '', description: '', s3Key: '' })
    const lessonSaving = ref(false)
    const lessonSaveError = ref('')

    // Lesson upload + extract
    const lessonUploadFile = ref<File | null>(null)
    const lessonUploadLoading = ref(false)
    const lessonUploadProgress = ref(0)
    const lessonUploadStatus = ref<'idle' | 'uploading' | 'extracting' | 'success' | 'error'>('idle')
    const lessonUploadErrorMsg = ref('')
    // ZIP lands in uploads/ prefix (temp) so it can be deleted after extraction
    const lessonUploadTargetKey = computed(() => {
      if (!lessonUploadFile.value || !courseId.value) return ''
      const lessonSlug = lessonForm.value.title
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'lektion'
      return `uploads/scorm/${courseId.value}/${lessonSlug}-${Date.now()}.zip`
    })

    // Bei einer NEUEN Lektion wird das ZIP zwar sofort hochgeladen, die Lektion
    // aber erst beim Speichern angelegt+extrahiert (sonst Duplikat). Hier den
    // hochgeladenen ZIP-Key bis zum Speichern merken.
    const uploadedZipKey = ref('')

    // Lesson delete
    const deleteLessonTarget = ref<CourseLesson | null>(null)
    const deletingLesson = ref(false)

    // Lesson reorder
    const reorderingLesson = ref(false)

    // Draft publish / discard (track which lessonId is in-flight)
    const publishingDraft = ref<string | null>(null)
    const discardingDraft = ref<string | null>(null)

    // True when editing an existing lesson that already has a live s3Key
    const lessonIsVersionUpdate = computed(() => {
      return !!(editingLesson.value && editingLesson.value.s3Key)
    })

    async function loadLessons() {
      if (!courseId.value) return
      lessonsLoading.value = true
      try {
        const jwt = store.getters['getJWT']
        lessons.value = await listLessons(courseId.value, jwt)
      } catch {
        // silent — lessons just won't show
      } finally {
        lessonsLoading.value = false
      }
    }

    function openAddLesson() {
      lessonModalMode.value = 'create'
      editingLesson.value = null
      lessonForm.value = { title: '', description: '', s3Key: '' }
      lessonUploadFile.value = null
      lessonUploadStatus.value = 'idle'
      uploadedZipKey.value = ''
      lessonSaveError.value = ''
      lessonModalVisible.value = true
    }

    function openEditLesson(lesson: CourseLesson) {
      lessonModalMode.value = 'edit'
      editingLesson.value = lesson
      lessonForm.value = { title: lesson.title, description: lesson.description, s3Key: lesson.s3Key }
      lessonUploadFile.value = null
      lessonUploadStatus.value = 'idle'
      lessonSaveError.value = ''
      lessonModalVisible.value = true
    }

    /** Opens the edit modal focused on uploading a new version (pre-selects upload tab) */
    function openNewVersionUpload(lesson: CourseLesson) {
      openEditLesson(lesson)
    }

    /** Open live lesson preview in a new tab */
    // ── SCORM in-app preview ──────────────────────────────────────────────
    const scormIframe = ref<HTMLIFrameElement | null>(null)
    const scormPreview = ref({
      visible: false,
      title: '',
      url: '',
      isDraft: false,
      error: false,
    })

    function buildContentUrl(s3Key: string): string {
      const base = getEnvironmentVariables().contentBase.replace(/\/$/, '')
      return base + '/' + s3Key
    }

    // ── Vorschaubild-Upload (gleiche S3-Mechanik wie SCORM) ──────────────
    const imageInput = ref<HTMLInputElement | null>(null)
    const imageUploadLoading = ref(false)
    const imageUploadProgress = ref(0)
    const imageUploadError = ref('')

    function triggerImageUpload() {
      imageInput.value?.click()
    }

    async function onImageSelected(e: Event) {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      imageUploadLoading.value = true
      imageUploadProgress.value = 0
      imageUploadError.value = ''
      try {
        const id = form.value.courseId || 'kurs'
        const safeName = file.name.replace(/[^\w.-]+/g, '_')
        const key = `course-images/${id}/${Date.now()}_${safeName}`
        const contentType = file.type || 'image/jpeg'
        const jwt = store.getters['getJWT']

        const { uploadUrl } = await store.dispatch('superAdmin/GET_UPLOAD_URL', { key, contentType, jwt })
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('PUT', uploadUrl)
          xhr.setRequestHeader('Content-Type', contentType)
          xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) imageUploadProgress.value = Math.round((ev.loaded / ev.total) * 100)
          }
          xhr.onload = () => (xhr.status === 200 ? resolve() : reject(new Error(`Upload fehlgeschlagen (Status ${xhr.status})`)))
          xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload'))
          xhr.send(file)
        })
        form.value.imageUrl = buildContentUrl(key)
      } catch (err: any) {
        imageUploadError.value = err?.message ?? 'Upload fehlgeschlagen'
      } finally {
        imageUploadLoading.value = false
        // erlaubt erneutes Hochladen derselben Datei
        if (imageInput.value) imageInput.value.value = ''
      }
    }

    function openScormPreview(title: string, s3Key: string, isDraft = false) {
      const url = buildContentUrl(s3Key)
      scormPreview.value = { visible: true, title, url, isDraft, error: false }
      // Install a minimal SCORM 1.2 API so packages don't block on init
      ;(window as any).API = {
        LMSInitialize: () => 'true',
        LMSFinish: () => 'true',
        LMSGetValue: () => '',
        LMSSetValue: () => 'true',
        LMSCommit: () => 'true',
        LMSGetLastError: () => '0',
        LMSGetErrorString: () => '',
        LMSGetDiagnostic: () => '',
      }
    }

    function closeScormPreview() {
      scormPreview.value.visible = false
      delete (window as any).API
    }

    function onScormIframeLoad() {
      // Detect S3 XML error responses by checking if the iframe loaded an XML error page
      try {
        const doc = scormIframe.value?.contentDocument
        if (doc && doc.querySelector('Error > Code')) {
          scormPreview.value.error = true
        }
      } catch {
        // cross-origin — content likely loaded fine
      }
    }

    function previewLesson(lesson: CourseLesson) {
      if (!lesson.s3Key) return
      openScormPreview(lesson.title, lesson.s3Key, false)
    }

    function previewDraft(lesson: CourseLesson) {
      if (!lesson.draftKey) return
      openScormPreview(lesson.title + ' (Entwurf)', lesson.draftKey, true)
    }

    /** Open the first live lesson of the course as a course-level preview */
    function previewCourse() {
      const firstLive = lessons.value.find(function(l) { return !!l.s3Key })
      if (!firstLive) return
      previewLesson(firstLive)
    }

    async function publishDraft(lesson: CourseLesson) {
      if (!courseId.value) return
      publishingDraft.value = lesson.lessonId
      try {
        const jwt = store.getters['getJWT']
        const updated = await publishDraftLesson(courseId.value, lesson.lessonId, jwt)
        lessons.value = lessons.value.map(function(l) { return l.lessonId === updated.lessonId ? updated : l })
      } catch (e: any) {
        // silent — row just stays in draft state; user can retry
        console.error('[publishDraft]', e)
      } finally {
        publishingDraft.value = null
      }
    }

    async function discardDraft(lesson: CourseLesson) {
      if (!courseId.value) return
      discardingDraft.value = lesson.lessonId
      try {
        const jwt = store.getters['getJWT']
        const updated = await discardDraftLesson(courseId.value, lesson.lessonId, jwt)
        lessons.value = lessons.value.map(function(l) { return l.lessonId === updated.lessonId ? updated : l })
      } catch (e: any) {
        console.error('[discardDraft]', e)
      } finally {
        discardingDraft.value = null
      }
    }

    function onLessonFileSelected(e: Event) {
      const file = (e.target as HTMLInputElement).files?.[0] ?? null
      lessonUploadFile.value = file
      lessonUploadStatus.value = 'idle'
      lessonUploadProgress.value = 0
      if (file) {
        uploadLessonToS3()
      }
    }

    async function uploadLessonToS3() {
      if (!lessonUploadFile.value || !courseId.value) return
      const zipKey = lessonUploadTargetKey.value
      if (!zipKey) return

      lessonUploadLoading.value = true
      lessonUploadProgress.value = 0
      lessonUploadStatus.value = 'uploading'
      lessonUploadErrorMsg.value = ''

      try {
        const jwt = store.getters['getJWT']

        // Step 1: get signed URL and upload ZIP
        const { uploadUrl } = await store.dispatch('superAdmin/GET_UPLOAD_URL', {
          key: zipKey,
          contentType: 'application/zip',
          jwt,
        })
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('PUT', uploadUrl)
          xhr.setRequestHeader('Content-Type', 'application/zip')
          xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) lessonUploadProgress.value = Math.round((ev.loaded / ev.total) * 100)
          }
          xhr.onload = () => (xhr.status === 200 ? resolve() : reject(new Error(`Upload fehlgeschlagen (Status ${xhr.status})`)))
          xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload'))
          xhr.send(lessonUploadFile.value)
        })

        // Step 2: trigger server-side extraction
        lessonUploadStatus.value = 'extracting'
        lessonUploadProgress.value = 100

        const lessonIdForExtract = editingLesson.value ? editingLesson.value.lessonId : null
        if (!lessonIdForExtract) {
          // Neue Lektion: ZIP ist hochgeladen, aber die Lektion wird erst beim
          // Klick auf „Speichern" angelegt und extrahiert – sonst entsteht ein
          // Duplikat (eine Lektion hier, eine beim Speichern).
          uploadedZipKey.value = zipKey
          lessonUploadStatus.value = 'success'
          return
        }

        // Existing lesson with live content → extract as draft so live stays intact
        const isDraft = !!(editingLesson.value && editingLesson.value.s3Key)
        const result = await extractScormLesson(courseId.value, lessonIdForExtract, zipKey, jwt, isDraft)
        lessons.value = lessons.value.map(function(l) { return l.lessonId === result.lesson.lessonId ? result.lesson : l })
        lessonForm.value.s3Key = result.lesson.s3Key
        lessonUploadStatus.value = 'success'
        // Keep modal open briefly so the success message shows, then close
        if (!isDraft) {
          lessonModalVisible.value = false
        }
      } catch (e: any) {
        lessonUploadStatus.value = 'error'
        lessonUploadErrorMsg.value = e?.response?.data?.error ?? e?.message ?? 'Unbekannter Fehler'
      } finally {
        lessonUploadLoading.value = false
      }
    }

    async function saveLesson() {
      if (!lessonForm.value.title || !courseId.value) return
      lessonSaving.value = true
      lessonSaveError.value = ''
      try {
        const jwt = store.getters['getJWT']
        if (lessonModalMode.value === 'create') {
          const created = await createLesson(courseId.value, {
            title: lessonForm.value.title,
            description: lessonForm.value.description,
            s3Key: '',
          }, jwt)
          if (uploadedZipKey.value) {
            // Genau EINMAL anlegen, dann das zuvor hochgeladene ZIP extrahieren
            const result = await extractScormLesson(courseId.value, created.lessonId, uploadedZipKey.value, jwt)
            lessons.value = [...lessons.value, result.lesson]
          } else {
            lessons.value = [...lessons.value, created]
          }
        } else if (editingLesson.value) {
          const updated = await updateLesson(courseId.value, editingLesson.value.lessonId, {
            title: lessonForm.value.title,
            description: lessonForm.value.description,
            s3Key: lessonForm.value.s3Key,
          }, jwt)
          lessons.value = lessons.value.map((l) => l.lessonId === updated.lessonId ? updated : l)
        }
        lessonModalVisible.value = false
      } catch {
        lessonSaveError.value = 'Speichern fehlgeschlagen.'
      } finally {
        lessonSaving.value = false
      }
    }

    // Nach Drag & Drop: neue Reihenfolge persistieren. vuedraggable hat lessons.value
    // bereits umsortiert; wir vergeben order = Index und speichern nur die geänderten.
    async function onLessonReorder() {
      reorderingLesson.value = true
      try {
        const jwt = store.getters['getJWT']
        const changed = lessons.value
          .map((l, i) => ({ l, i }))
          .filter(({ l, i }) => l.order !== i)
        // Optimistisch lokal normalisieren (Anzeige bleibt stabil)
        lessons.value = lessons.value.map((l, i) => ({ ...l, order: i }))
        if (changed.length === 0) return
        await Promise.all(changed.map(({ l, i }) =>
          updateLesson(courseId.value!, l.lessonId, {
            title: l.title, description: l.description, s3Key: l.s3Key, order: i,
          }, jwt),
        ))
      } catch {
        // Bei Fehler: echten Stand neu laden, damit UI und Server konsistent sind
        await loadLessons()
      } finally {
        reorderingLesson.value = false
      }
    }

    // Info-Bubble zu SCORM-Versionen im Lektions-Modal
    const scormInfoOpen = ref(false)

    // ── Lektions-Kontextmenü ─────────────────────────────────
    const openMenuId = ref<string | null>(null)
    function toggleMenu(id: string) {
      openMenuId.value = openMenuId.value === id ? null : id
    }
    function closeMenu() {
      openMenuId.value = null
    }
    function onDocumentClick() {
      if (openMenuId.value) openMenuId.value = null
    }
    onMounted(() => document.addEventListener('click', onDocumentClick))
    onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick))

    function confirmDeleteLesson(lesson: CourseLesson) {
      deleteLessonTarget.value = lesson
    }

    async function doDeleteLesson() {
      if (!deleteLessonTarget.value || !courseId.value) return
      deletingLesson.value = true
      try {
        const jwt = store.getters['getJWT']
        await deleteLesson(courseId.value, deleteLessonTarget.value.lessonId, jwt)
        lessons.value = lessons.value.filter((l) => l.lessonId !== deleteLessonTarget.value!.lessonId)
        deleteLessonTarget.value = null
      } catch {
        // silent
      } finally {
        deletingLesson.value = false
      }
    }

    onMounted(async () => {
      if (isEdit.value && courseId.value) {
        const courses: CourseCatalogItem[] = store.getters['superAdmin/getCourses']
        let course = courses.find((c) => c.courseId === courseId.value)
        if (!course) {
          await store.dispatch('superAdmin/LOAD_COURSES')
          course = store.getters['superAdmin/getCourses'].find((c: CourseCatalogItem) => c.courseId === courseId.value)
        }
        if (course) {
          form.value = { ...course }
        } else {
          loadError.value = 'Kurs nicht gefunden.'
        }
        await Promise.all([loadVisibility(), loadLessons()])
      }
    })

    async function save() {
      saveError.value = null
      saveSuccess.value = false

      if (!form.value.title) {
        saveError.value = 'Bitte einen Titel eingeben.'
        return
      }
      if (!isEdit.value && !form.value.courseId) {
        saveError.value = 'Bitte eine Kurs-ID vergeben.'
        return
      }

      saving.value = true
      try {
        if (isEdit.value) {
          // Kurs-Metadaten und Org-Sichtbarkeit gemeinsam speichern
          await store.dispatch('superAdmin/UPDATE_COURSE', form.value)
          await saveVisibility()
        } else {
          await store.dispatch('superAdmin/CREATE_COURSE', form.value)
          router.push('/superadmin/courses')
          return
        }
        saveSuccess.value = true
      } catch (e: any) {
        saveError.value = 'Speichern fehlgeschlagen. Bitte erneut versuchen.'
      } finally {
        saving.value = false
      }
    }

    function onFileSelected(e: Event) {
      const file = (e.target as HTMLInputElement).files?.[0] ?? null
      uploadFile.value = file
      uploadStatus.value = 'idle'
      uploadProgress.value = 0
    }

    async function uploadToS3() {
      if (!uploadFile.value) return
      const key = uploadTargetKey.value
      if (!key) { saveError.value = 'Bitte zuerst einen S3-Key oder eine Kurs-ID angeben.'; return }

      uploadLoading.value = true
      uploadProgress.value = 0
      uploadStatus.value = 'idle'

      try {
        const jwt = store.getters['getJWT']
        const { uploadUrl } = await store.dispatch('superAdmin/GET_UPLOAD_URL', {
          key,
          contentType: uploadFile.value.type || 'application/octet-stream',
          jwt,
        })

        // Upload directly to S3 via XHR (for progress events)
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('PUT', uploadUrl)
          xhr.setRequestHeader('Content-Type', uploadFile.value!.type || 'application/octet-stream')
          xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) uploadProgress.value = Math.round((ev.loaded / ev.total) * 100)
          }
          xhr.onload = () => (xhr.status === 200 ? resolve() : reject(new Error(`Status ${xhr.status}`)))
          xhr.onerror = () => reject(new Error('Netzwerkfehler'))
          xhr.send(uploadFile.value)
        })

        // Auto-set s3Key on success
        form.value.s3Key = key
        uploadStatus.value = 'success'
        uploadProgress.value = 100
      } catch {
        uploadStatus.value = 'error'
      } finally {
        uploadLoading.value = false
      }
    }

    async function loadVisibility() {
      visLoading.value = true
      try {
        const jwt = store.getters['getJWT']
        const data = await store.dispatch('superAdmin/GET_VISIBILITY', { courseId: courseId.value, jwt })
        if (data.orgIds.includes('*') || data.orgIds.length === 0) {
          visForAll.value = true
          selectedOrgs.value = []
        } else {
          visForAll.value = false
          // Pre-fill selected orgs — load all orgs if not yet loaded
          if (!store.getters['superAdmin/getAllOrganisations'].length) {
            await store.dispatch('superAdmin/LOAD_ALL_ORGANISATIONS')
          }
          selectedOrgs.value = store.getters['superAdmin/getAllOrganisations']
            .filter((o: OrgListItem) => data.orgIds.includes(o.organisationId))
        }
      } catch {
        visForAll.value = true
        selectedOrgs.value = []
      } finally {
        visLoading.value = false
      }
    }

    async function saveVisibility() {
      visLoading.value = true
      try {
        const jwt = store.getters['getJWT']
        const orgIds = visForAll.value
          ? ['*']
          : selectedOrgs.value.map((o) => o.organisationId)
        await store.dispatch('superAdmin/SET_VISIBILITY', { courseId: courseId.value, orgIds, jwt })
      } finally {
        visLoading.value = false
      }
    }

    return {
      isEdit,
      form,
      loadError,
      saveError,
      saveSuccess,
      saving,
      // Upload
      uploadFile,
      uploadLoading,
      uploadProgress,
      uploadStatus,
      uploadTargetKey,
      onFileSelected,
      uploadToS3,
      // Vorschaubild-Upload
      imageInput,
      imageUploadLoading,
      imageUploadProgress,
      imageUploadError,
      triggerImageUpload,
      onImageSelected,
      // Org Visibility
      visForAll,
      selectedOrgs,
      visLoading,
      orgPickerVisible,
      orgPickerLoading,
      orgSearch,
      filteredPickerOrgs,
      isOrgSelected,
      toggleOrg,
      removeOrg,
      openOrgPicker,
      save,
      loadVisibility,
      saveVisibility,
      // Lessons
      lessons,
      lessonsLoading,
      lessonModalVisible,
      lessonModalMode,
      lessonForm,
      lessonSaving,
      lessonSaveError,
      lessonUploadFile,
      lessonUploadLoading,
      lessonUploadProgress,
      lessonUploadStatus,
      lessonUploadErrorMsg,
      lessonUploadTargetKey,
      lessonIsVersionUpdate,
      deleteLessonTarget,
      deletingLesson,
      reorderingLesson,
      publishingDraft,
      discardingDraft,
      openAddLesson,
      openEditLesson,
      openNewVersionUpload,
      onLessonFileSelected,
      uploadLessonToS3,
      saveLesson,
      confirmDeleteLesson,
      doDeleteLesson,
      onLessonReorder,
      scormInfoOpen,
      openMenuId,
      toggleMenu,
      closeMenu,
      previewLesson,
      previewDraft,
      previewCourse,
      publishDraft,
      discardDraft,
      scormPreview,
      scormIframe,
      closeScormPreview,
      onScormIframeLoad,
    }
  },
})
